import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Markdown } from 'tiptap-markdown';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';

/** Curated palette for font color + highlight pickers — calibrated for
 *  the dark theme. Matches the project-color picker vibe so the editor
 *  feels consistent with the rest of the app. */
const NOTE_COLORS: { id: string; label: string; value: string }[] = [
  { id: 'default', label: 'Default', value: '' },
  { id: 'red', label: 'Red', value: 'oklch(0.7 0.2 25)' },
  { id: 'amber', label: 'Amber', value: 'oklch(0.78 0.16 75)' },
  { id: 'emerald', label: 'Emerald', value: 'oklch(0.72 0.16 155)' },
  { id: 'cyan', label: 'Cyan', value: 'oklch(0.74 0.13 210)' },
  { id: 'periwinkle', label: 'Periwinkle', value: 'oklch(0.68 0.19 265)' },
  { id: 'magenta', label: 'Magenta', value: 'oklch(0.68 0.22 340)' },
];

const NOTE_HIGHLIGHTS: { id: string; label: string; value: string }[] = [
  { id: 'default', label: 'None', value: '' },
  { id: 'red', label: 'Red', value: 'oklch(0.5 0.18 25 / 0.5)' },
  { id: 'amber', label: 'Amber', value: 'oklch(0.7 0.15 75 / 0.4)' },
  { id: 'emerald', label: 'Emerald', value: 'oklch(0.55 0.16 155 / 0.4)' },
  { id: 'cyan', label: 'Cyan', value: 'oklch(0.6 0.13 210 / 0.4)' },
  { id: 'periwinkle', label: 'Periwinkle', value: 'oklch(0.55 0.19 265 / 0.5)' },
  { id: 'magenta', label: 'Magenta', value: 'oklch(0.55 0.2 340 / 0.45)' },
];

interface Props {
  initialMarkdown: string;
  placeholder?: string;
  autoFocus?: boolean;
  /** Called whenever the doc changes. value is markdown. */
  onChange?: (md: string) => void;
  /** Disable input. */
  disabled?: boolean;
  /** Min height in px. */
  minHeightPx?: number;
}

/**
 * Rich-text editor for project notes — TipTap with starter-kit
 * (Bold/Italic/Headings/Lists/Blockquote/Code) + Link extension.
 * Markdown extension serializes back to markdown so we stay storage-
 * compatible with body_md (no schema change required).
 *
 * Toolbar is a compact pill row above the editor.
 */
export function NoteEditor({
  initialMarkdown,
  placeholder,
  autoFocus,
  onChange,
  disabled,
  minHeightPx = 100,
}: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Drop heading variants we don't want — H1 is reserved for the
        // page title elsewhere; H2/H3 are enough for note structure.
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'underline underline-offset-2 text-primary',
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Markdown.configure({
        // Allow HTML so pasted content from Google Docs / Notion / web
        // pages survives the markdown round-trip (without this, headings
        // and bold from rich sources get flattened to plain text).
        html: true,
        tightLists: true,
        linkify: true,
        // `breaks: false` is critical — with breaks=true, single Enter
        // produces a soft <br> inside the SAME paragraph, so headings
        // (which are block-level) convert the entire paragraph including
        // every line below it. With breaks=false, each Enter creates a
        // new paragraph node, so H1 applies only to the paragraph the
        // cursor is on. Shift+Enter still gives a soft line break.
        breaks: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      ...(placeholder
        ? [
            Placeholder.configure({
              placeholder,
              emptyEditorClass:
                'before:absolute before:pointer-events-none before:text-muted-foreground/60 before:content-[attr(data-placeholder)]',
            }),
          ]
        : []),
    ],
    content: initialMarkdown,
    autofocus: autoFocus,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-invert prose-sm max-w-none focus:outline-none',
          'prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1',
          'prose-headings:font-semibold prose-a:text-primary',
          // CRITICAL: Tailwind Typography's prose-invert sets explicit
          // color on strong/em/code/headings, which overrides the color
          // inherited from a wrapping <span style="color:…">. Force inherit
          // so colored bold/italic/headings actually take the chosen color.
          'prose-strong:text-inherit prose-em:text-inherit prose-code:text-inherit prose-blockquote:text-inherit prose-headings:text-inherit',
        ),
        style: `min-height: ${minHeightPx}px`,
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      // Emit HTML, not Markdown. Markdown round-trip drops TextStyle
      // (color) + Highlight marks because neither has a markdown syntax.
      // The renderer (MarkdownView) detects HTML vs markdown source and
      // routes accordingly so existing markdown notes still render.
      onChange?.(editor.getHTML());
    },
  });

  // Keep the editor's editable state in sync if the parent toggles disabled.
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  return (
    <div>
      {editor && <Toolbar editor={editor} disabled={disabled} />}
      <EditorContent
        editor={editor}
        className="no-glow rounded-md bg-card px-3 py-2 text-sm text-foreground"
      />
    </div>
  );
}

function Toolbar({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  const btnClass = (active: boolean) =>
    cn(
      'rounded px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors',
      active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      disabled && 'pointer-events-none opacity-40',
    );

  /** CRITICAL: preventDefault on mousedown so the editor never blurs when
   *  the toolbar button is clicked. Without this, clicking Bold collapses
   *  the user's selection to the cursor position (or worse, the editor
   *  re-focuses to the document start and applies formatting to everything).
   *  This is the well-known TipTap toolbar gotcha. */
  function ToolbarButton({
    title,
    active,
    onAct,
    children,
  }: {
    title: string;
    active: boolean;
    onAct: () => void;
    children: React.ReactNode;
  }) {
    return (
      <button
        type="button"
        title={title}
        aria-label={title}
        aria-pressed={active}
        onMouseDown={(e) => e.preventDefault()}
        onClick={onAct}
        className={btnClass(active)}
      >
        {children}
      </button>
    );
  }

  function setLink() {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL (empty to remove):', prev ?? '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
  }

  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      className="mb-1 flex flex-wrap items-center gap-0.5 border-b border-border/40 pb-1.5"
    >
      <ToolbarButton title="Bold (⌘B)" active={editor.isActive('bold')} onAct={() => editor.chain().focus().toggleBold().run()}>
        <span className="font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton title="Italic (⌘I)" active={editor.isActive('italic')} onAct={() => editor.chain().focus().toggleItalic().run()}>
        <span className="italic">I</span>
      </ToolbarButton>
      <ToolbarButton title="Strike" active={editor.isActive('strike')} onAct={() => editor.chain().focus().toggleStrike().run()}>
        <span className="line-through">S</span>
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border/60" aria-hidden />
      <ToolbarButton title="Heading large" active={editor.isActive('heading', { level: 2 })} onAct={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
        H1
      </ToolbarButton>
      <ToolbarButton title="Heading small" active={editor.isActive('heading', { level: 3 })} onAct={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
        H2
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border/60" aria-hidden />
      <ToolbarButton title="Bullet list" active={editor.isActive('bulletList')} onAct={() => editor.chain().focus().toggleBulletList().run()}>
        •
      </ToolbarButton>
      <ToolbarButton title="Numbered list" active={editor.isActive('orderedList')} onAct={() => editor.chain().focus().toggleOrderedList().run()}>
        1.
      </ToolbarButton>
      <ToolbarButton title="Quote" active={editor.isActive('blockquote')} onAct={() => editor.chain().focus().toggleBlockquote().run()}>
        ❝
      </ToolbarButton>
      <ToolbarButton title="Code" active={editor.isActive('code')} onAct={() => editor.chain().focus().toggleCode().run()}>
        {'</>'}
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border/60" aria-hidden />
      <ToolbarButton title="Link (⌘K)" active={editor.isActive('link')} onAct={setLink}>
        🔗
      </ToolbarButton>
      <span className="mx-1 h-4 w-px bg-border/60" aria-hidden />
      {/* Font color — small swatch row. Default clears to inherit
          (theme foreground / overrides). */}
      <ColorSwatches
        label="Text color"
        swatches={NOTE_COLORS}
        currentValue={(editor.getAttributes('textStyle').color as string) ?? ''}
        onPick={(v) => {
          if (v) editor.chain().focus().setColor(v).run();
          else editor.chain().focus().unsetColor().run();
        }}
      />
      <ColorSwatches
        label="Highlight"
        ringOnly
        swatches={NOTE_HIGHLIGHTS}
        currentValue={(editor.getAttributes('highlight').color as string) ?? ''}
        onPick={(v) => {
          if (v) editor.chain().focus().toggleHighlight({ color: v }).run();
          else editor.chain().focus().unsetHighlight().run();
        }}
      />
      <span className="mx-1 h-4 w-px bg-border/60" aria-hidden />
      <ToolbarButton title="Undo" active={false} onAct={() => editor.chain().focus().undo().run()}>
        ↶
      </ToolbarButton>
      <ToolbarButton title="Redo" active={false} onAct={() => editor.chain().focus().redo().run()}>
        ↷
      </ToolbarButton>
    </div>
  );
}

/** Inline swatch picker for font color OR highlight. Tap a swatch =
 *  apply; tap "Default" / "None" = clear. preventDefault on mousedown
 *  matches ToolbarButton so the editor selection is preserved. */
function ColorSwatches({
  label,
  swatches,
  currentValue,
  onPick,
  ringOnly,
}: {
  label: string;
  swatches: { id: string; label: string; value: string }[];
  currentValue: string;
  onPick: (value: string) => void;
  /** For highlight: render swatches as filled chips (transparent for None) */
  ringOnly?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      title={label}
      className="flex items-center gap-0.5 rounded px-1"
    >
      {swatches.map((s) => {
        const isClear = s.value === '';
        const selected = currentValue === s.value;
        return (
          <button
            key={s.id}
            type="button"
            title={`${label}: ${s.label}`}
            aria-label={`${label}: ${s.label}`}
            aria-pressed={selected}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(s.value)}
            className={cn(
              'no-glow size-4 shrink-0 rounded-full border transition-all',
              selected ? 'ring-2 ring-foreground/60 scale-110' : 'opacity-80 hover:opacity-100 hover:scale-105',
              isClear ? 'border-dashed border-muted-foreground/40 bg-transparent' : ringOnly ? 'border-transparent' : 'border-transparent',
            )}
            style={isClear ? undefined : { background: s.value }}
          >
            {isClear && (
              <span className="block size-full" aria-hidden>
                <svg viewBox="0 0 16 16" className="size-full text-muted-foreground/60">
                  <line x1="2" y1="14" x2="14" y2="2" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
